/**
 * `<caos-code-editor>` — a code surface with a line gutter, for editing one platform artifact.
 * (Catalog: Editors & builders › CodeEditor — one code artifact, with a line gutter, edited in place)
 *
 * CAOS-493. The acceptance criterion this file is written against is "the editor engine is an
 * implementation detail behind the component contract", so the contract comes first and the engine
 * is chosen behind it:
 *
 *   - A PAGE only ever sees `value`, `language`, `readonly`, `dirty`, `change` and `save`. None of
 *     those name an engine, a keymap, a mode, or a syntax tree, so none of them has to change when
 *     the engine does.
 *   - An ENGINE only ever sees {@link CodeEditorEngine} — mount, read, write, set read-only,
 *     focus, destroy. It is handed a container and told nothing about the platform.
 *
 * The engine shipped here is a textarea with a gutter drawn beside it. That is a deliberate choice
 * and not a placeholder to be embarrassed about: it needs no third-party bundle, it is the only
 * option that works with no network at all, it is the one every assistive technology already
 * understands, and native undo/redo, find, IME composition and spellcheck suppression all come for
 * free and are each a thing a hand-built editor gets wrong. A richer engine — highlighting, folding,
 * multiple cursors — registers itself with {@link registerCodeEditorEngine} and every page that
 * already embeds this component gets it without editing one line of a page.
 *
 * What is deliberately NOT here: this component does not fetch, save, diff, or know what an artifact
 * is. It raises `save` and lets the surface decide, because the platform's own write path requires a
 * `componentVersion` to be stated back with the edit (`metadata/component-upsert`), and a component
 * that fetched and wrote on its own would be a second, unvalidated door onto the deploy pipeline.
 * See `kernel/src/api/metadata-routes.ts`, "the per-component Tooling API".
 *
 * Styling seams: `::part(surface)`, `::part(gutter)`, `::part(input)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { applyAccessibleName } from '../accessible-name.js';

/**
 * What an editor engine must do, and the whole of what this component asks of one.
 *
 * Note what is absent: no highlighting hook, no theme object, no token stream. An engine that wants
 * to highlight does so inside its own container with its own colours; the component does not mediate
 * it, because a seam that describes HOW an engine draws is a seam the next engine cannot fit.
 */
export interface CodeEditorEngine {
  /**
   * Take over `container` and draw an editing surface in it. Called once, on connect.
   *
   * `onInput` must be called on every edit the person makes — it is what makes the component dirty
   * and what raises `change`. An engine that batches or debounces internally is free to, but a
   * `change` that never arrives reads on a page as an edit that did not happen.
   *
   * `onSave` is the explicit save gesture (Ctrl/Cmd+S). An engine that cannot offer one omits it
   * and the surface keeps its own save control; nothing here requires the keystroke to exist.
   */
  mount(container: HTMLElement, opts: CodeEditorEngineOptions): void;
  getValue(): string;
  setValue(value: string): void;
  setReadOnly(readOnly: boolean): void;
  /**
   * The code is now said to be a different language. OPTIONAL, because the engine that ships does
   * not highlight and has nothing to do with it — but an engine that picks a grammar from the
   * language needs to hear about a page that changes it, and without this the attribute would be
   * observed, changeable, and silently only ever read once.
   */
  setLanguage?(language: string): void;
  focus(): void;
  /** Release listeners and DOM. Called on disconnect, and before a different engine takes over. */
  destroy(): void;
}

export interface CodeEditorEngineOptions {
  readonly initialValue: string;
  readonly readOnly: boolean;
  /** Informational: an engine that highlights may read it, and one that does not ignores it. */
  readonly language: string;
  readonly onInput: () => void;
  readonly onSave: () => void;
}

/** Builds an engine. Registered globally so a page embeds `<caos-code-editor>` and nothing else. */
export type CodeEditorEngineFactory = () => CodeEditorEngine;

const sheet = makeSheet(`
  :host {
    display: block;
    min-height: 0;
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    overflow: hidden;
  }
  :host([hidden]) { display: none !important; }

  .surface {
    display: flex;
    align-items: stretch;
    height: 100%;
    min-height: 0;
    /* One type ramp for the gutter and the code, so line N in the gutter sits on line N of the
       code. Any divergence here shows up as drift that grows down the file. */
    font-family: var(--caos-font-mono);
    font-size: 12px;
    line-height: 20px;
  }

  .gutter {
    flex: 0 0 auto;
    padding: 8px 8px 8px 12px;
    margin: 0;
    text-align: right;
    color: var(--caos-color-text-muted);
    background: var(--caos-color-surface-2);
    border-right: 1px solid var(--caos-color-border);
    user-select: none;
    overflow: hidden;
    white-space: pre;
    font: inherit;
  }

  .input {
    flex: 1 1 auto;
    min-width: 0;
    appearance: none;
    border: 0;
    outline: none;
    resize: none;
    padding: 8px 12px;
    margin: 0;
    color: var(--caos-color-text);
    background: transparent;
    font: inherit;
    tab-size: 2;
    white-space: pre;
    overflow: auto;
  }
  .input:focus-visible { box-shadow: var(--caos-focus-ring); }
  .input[readonly] { color: var(--caos-color-text-muted); }
`);

/**
 * The engine that ships: a textarea, and a gutter kept in step with it.
 *
 * The gutter scrolls with the textarea rather than being inside it, which is the one piece of real
 * work here. `white-space: pre` on both, one shared line-height, and the gutter's scrollTop follows
 * the textarea's — so a wrapped line cannot desynchronise them, because nothing wraps.
 */
class TextareaEngine implements CodeEditorEngine {
  private textarea: HTMLTextAreaElement | null = null;
  private gutter: HTMLElement | null = null;
  private onInput: (() => void) | null = null;
  private onSave: (() => void) | null = null;

  mount(container: HTMLElement, opts: CodeEditorEngineOptions): void {
    const gutter = document.createElement('pre');
    gutter.className = 'gutter';
    gutter.setAttribute('part', 'gutter');
    gutter.setAttribute('aria-hidden', 'true');

    const textarea = document.createElement('textarea');
    textarea.className = 'input';
    textarea.setAttribute('part', 'input');
    textarea.value = opts.initialValue;
    textarea.readOnly = opts.readOnly;
    // A code surface is not prose: none of these help, and all of them corrupt what is typed.
    textarea.spellcheck = false;
    textarea.autocapitalize = 'off';
    textarea.setAttribute('autocorrect', 'off');
    textarea.setAttribute('autocomplete', 'off');
    textarea.wrap = 'off';
    // A natural height, for a parent that gives it none. A textarea defaults to two rows, and two
    // rows of a file is not a code editor; a parent WITH a height still wins, because the surface
    // stretches inside it.
    textarea.rows = 12;

    this.textarea = textarea;
    this.gutter = gutter;
    this.onInput = opts.onInput;
    this.onSave = opts.onSave;

    textarea.addEventListener('input', this.handleInput);
    textarea.addEventListener('scroll', this.handleScroll);
    textarea.addEventListener('keydown', this.handleKeydown);

    container.replaceChildren(gutter, textarea);
    this.drawGutter();
  }

  getValue(): string {
    return this.textarea?.value ?? '';
  }

  setValue(value: string): void {
    if (!this.textarea || this.textarea.value === value) return;
    this.textarea.value = value;
    this.drawGutter();
  }

  setReadOnly(readOnly: boolean): void {
    if (this.textarea) this.textarea.readOnly = readOnly;
  }

  focus(): void {
    this.textarea?.focus();
  }

  destroy(): void {
    this.textarea?.removeEventListener('input', this.handleInput);
    this.textarea?.removeEventListener('scroll', this.handleScroll);
    this.textarea?.removeEventListener('keydown', this.handleKeydown);
    this.textarea = null;
    this.gutter = null;
    this.onInput = null;
    this.onSave = null;
  }

  private readonly handleInput = (): void => {
    this.drawGutter();
    this.onInput?.();
  };

  private readonly handleScroll = (): void => {
    if (this.gutter && this.textarea) this.gutter.scrollTop = this.textarea.scrollTop;
  };

  private readonly handleKeydown = (e: KeyboardEvent): void => {
    // Ctrl/Cmd+S is the save gesture every editor has trained people to expect. Taken here so the
    // browser does not offer to save the page instead, which is what it does otherwise.
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
      e.preventDefault();
      this.onSave?.();
    }
  };

  /**
   * A line number per line. Counted off the value rather than off rendered rows: nothing wraps, so
   * the two agree, and counting rendered rows would make the gutter a function of the width.
   */
  private drawGutter(): void {
    if (!this.gutter || !this.textarea) return;
    const lines = this.textarea.value.split('\n').length;
    let out = '';
    for (let n = 1; n <= lines; n += 1) out += `${n}\n`;
    this.gutter.textContent = out;
  }
}

let engineFactory: CodeEditorEngineFactory = () => new TextareaEngine();
/** False while the default above stands. See {@link isCodeEditorEngineRegistered}. */
let applicationRegistered = false;

/**
 * Swap the engine every `<caos-code-editor>` builds from.
 *
 * Call it before the first editor connects. Editors already on the page keep the engine they
 * mounted with, because tearing a live editor down under somebody's cursor would lose the selection
 * and the undo stack — a page that wants the new engine in an open editor re-creates the element.
 */
export function registerCodeEditorEngine(factory: CodeEditorEngineFactory): void {
  engineFactory = factory;
  applicationRegistered = true;
}

/**
 * Whether an application has actually registered an engine, as opposed to the platform's default
 * standing unclaimed.
 *
 * CAOS-499's code surfaces need to tell those apart: unclaimed, they mount their own plain-text
 * surface, which can search and takes a run shortcut; claimed, they must defer to the application's
 * choice like every other editing surface, capabilities or not.
 */
export function isCodeEditorEngineRegistered(): boolean {
  return applicationRegistered;
}

/**
 * Build the registered engine, for a surface that embeds an editor without being one.
 *
 * CAOS-499 added three more places a person types code — the query editor's tabs and the REST
 * console's body pane among them — and they must mount the SAME engine an application registered
 * here. Exposing the factory's product keeps one registration point: without it each of those
 * surfaces would need its own, and installing an editor would mean remembering all of them.
 */
export function buildRegisteredCodeEditorEngine(): CodeEditorEngine {
  return engineFactory();
}

/** The engine shipped with the platform, exported so a replacement can defer to it. */
export { TextareaEngine as CaosTextareaCodeEditorEngine };

export class CaosCodeEditor extends CaosElement {
  static get observedAttributes(): string[] {
    return ['readonly', 'language', 'aria-label'];
  }

  private surface: HTMLDivElement;
  private engine: CodeEditorEngine | null = null;
  /** Held until the engine mounts, so `value` can be set before the element is on the document. */
  private pendingValue = '';
  private baseline = '';

  constructor() {
    super(sheet);
    this.surface = document.createElement('div');
    this.surface.className = 'surface';
    this.surface.setAttribute('part', 'surface');
    this.root.append(this.surface);
  }

  connectedCallback(): void {
    if (!this.engine) {
      this.engine = engineFactory();
      this.engine.mount(this.surface, {
        initialValue: this.pendingValue,
        readOnly: this.readOnly,
        language: this.language,
        onInput: () => this.handleInput(),
        onSave: () => this.requestSave(),
      });
    }
    this.syncName();
  }

  disconnectedCallback(): void {
    // Keep the text, drop the engine: a row that is detached and re-attached (a tab swap, a list
    // re-render) must come back with what the person had typed, not with what it was handed.
    this.pendingValue = this.engine?.getValue() ?? this.pendingValue;
    this.engine?.destroy();
    this.engine = null;
  }

  attributeChangedCallback(name: string): void {
    if (name === 'aria-label') {
      this.syncName();
      return;
    }
    if (name === 'readonly') this.engine?.setReadOnly(this.readOnly);
    if (name === 'language') this.engine?.setLanguage?.(this.language);
  }

  private syncName(): void {
    applyAccessibleName(this.surface, this.getAttribute('aria-label'), 'Code editor');
  }

  /** The code. A property and not an attribute: an artifact does not belong in the DOM as text. */
  get value(): string {
    return this.engine ? this.engine.getValue() : this.pendingValue;
  }
  set value(next: string) {
    this.pendingValue = next;
    this.engine?.setValue(next);
    // Setting the value is the page saying "this is what the artifact now is" — after a load, or
    // after a save that succeeded. Either way the editor is no longer ahead of the stored copy.
    this.baseline = next;
  }

  get language(): string {
    return this.getAttribute('language') ?? '';
  }
  set language(v: string) {
    this.setAttribute('language', v);
  }

  get readOnly(): boolean {
    return this.hasAttribute('readonly');
  }
  set readOnly(v: boolean) {
    if (v) this.setAttribute('readonly', '');
    else this.removeAttribute('readonly');
  }

  /**
   * Whether the code differs from the last value the page set.
   *
   * Compared against that baseline rather than tracked as a flag set by the first keystroke, so
   * typing a character and deleting it again leaves the editor clean — which is what the person
   * did, and a "you have unsaved changes" prompt over an unchanged file trains people to dismiss it.
   */
  get dirty(): boolean {
    return this.value !== this.baseline;
  }

  /** Put the cursor in the code. */
  focusEditor(): void {
    this.engine?.focus();
  }

  private handleInput(): void {
    this.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        composed: true,
        detail: { value: this.value, dirty: this.dirty },
      }),
    );
  }

  private requestSave(): void {
    if (this.readOnly) return;
    this.dispatchEvent(
      new CustomEvent('save', {
        bubbles: true,
        composed: true,
        detail: { value: this.value },
      }),
    );
  }
}

define('caos-code-editor', CaosCodeEditor);

declare global {
  interface HTMLElementTagNameMap {
    'caos-code-editor': CaosCodeEditor;
  }
}
